/**
 * Motor de links internos (E5.4).
 *
 * Insere, de forma determinística, os links que hoje os concorrentes fazem à
 * mão em cada post: 1 para a LP do cluster, 1 para o pilar e 3 a 6 para
 * satélites (ver `content/competitors/<concorrente>/summary.json`, campo `inboundLinks`,
 * e o `$comment` de `content/clusters.json`). A âncora nunca é inventada — sai
 * de um trecho que já existe no texto — exceto no link para a LP, cuja
 * exceção está descrita abaixo.
 *
 * IMPORTANTE — por que não usar um parser de DOM (jsdom/linkedom/etc.):
 * este módulo roda no SSR em Node (função serverless) e será portado depois
 * para o `content-audit` em .NET (E7.2). Um parser de DOM real amarraria
 * a lógica a uma API que não existe do outro lado da portabilidade, e o jsdom
 * especificamente já quebra o build serverless (ver o comentário no topo de
 * `sanitizeHtml.server.ts`). Por isso o parser aqui é um tokenizador simples,
 * escrito à mão, que assume o mesmo contrato que `sanitizeHtml.server.ts` já
 * assume: o HTML de entrada já passou pelo sanitizador, então o conjunto de
 * tags é fechado e conhecido (`sanitizePolicy.ts`) e não há tag malformada,
 * comentário, CDATA ou `<` solto dentro de texto (o sanitizador já teria
 * escapado isso como `&lt;`). Fora desse contrato o resultado não é garantido
 * — não é um parser de HTML genérico.
 */

/** Alvo de link interno: LP do cluster, post pilar ou post satélite. */
export interface LinkTarget {
  slug: string;
  title: string;
  keywords: string[];
  role: "pillar" | "satellite" | "landing";
}

export interface LinkPlan {
  html: string;
  inserted: Array<{ href: string; anchor: string; role: string }>;
  skipped: Array<{ href: string; reason: string }>;
}

export interface PlanInternalLinksOptions {
  /** Quando true, devolve o plano sem alterar o HTML (usado pelo job mensal — E7.2). */
  dryRun?: boolean;
  /** Mínimo de satélites antes de reportar déficit em `skipped`. Padrão: 3. */
  minSatellites?: number;
  /** Máximo de satélites a inserir. Padrão: 6. */
  maxSatellites?: number;
}

const DEFAULT_MIN_SATELLITES = 3;
const DEFAULT_MAX_SATELLITES = 6;

// -----------------------------------------------------------------------
// Tokenizador
// -----------------------------------------------------------------------

/**
 * Um "segmento" é ou uma tag (`<...>`) ou um trecho de texto corrido. O plano
 * inteiro trabalha em cima desta lista: inserir um link é trocar 1 segmento
 * de texto por 5 (texto-antes, `<a>`, texto-âncora, `</a>`, texto-depois), o
 * que mantém o restante do documento intocado e barato de re-serializar
 * (`segments.map(s => s.value).join("")`).
 */
type Segment =
  | { type: "tag"; value: string }
  | { type: "text"; value: string; protected: boolean };

// Tags que nunca podem ganhar um link por dentro (regra 4 da task). `a` está
// aqui não porque precise de "proteção" no sentido de bloco proibido, mas
// porque o próprio HTML não permite `<a>` dentro de `<a>` — tratar como
// protegido evita link aninhado da mesma forma.
const NON_LINKABLE_TAGS = new Set(["a", "h2", "h3", "code"]);

// Tags que não abrem contexto (sem filho, sem fechamento correspondente).
const VOID_TAGS = new Set(["br", "img"]);

const TOKEN_RE = /<[^>]+>|[^<]+/g;

const openTagName = (raw: string): string | null => {
  const m = /^<\/?([a-zA-Z][a-zA-Z0-9-]*)/.exec(raw);
  return m ? m[1].toLowerCase() : null;
};

const isClosingTag = (raw: string): boolean => raw.startsWith("</");

const getAttr = (raw: string, name: string): string | null =>
  new RegExp(`${name}\\s*=\\s*"([^"]*)"`, "i").exec(raw)?.[1] ?? null;

/**
 * Percorre o HTML uma vez, mantendo uma pilha das tags abertas, e marca cada
 * trecho de texto como `protected` se ele estiver, na hora em que aparece,
 * dentro de `a`, `h2`, `h3`, `code` ou de um
 * `<div data-ds-block="copy-block">`. É a checagem de contexto pedida na
 * regra 4 — feita com uma pilha em vez de regex direto no texto porque regex
 * não sabe "aninhamento": um `indexOf("<a")` isolado não diferencia "estou
 * dentro do link" de "o link já fechou há duas frases".
 */
const buildSegments = (html: string): Segment[] => {
  const tokens = html.match(TOKEN_RE) ?? [];
  const segments: Segment[] = [];
  const stack: boolean[] = []; // cada posição: a tag correspondente protege?
  let protectedDepth = 0;

  for (const token of tokens) {
    if (token.startsWith("<")) {
      segments.push({ type: "tag", value: token });

      const tagName = openTagName(token);
      if (!tagName || VOID_TAGS.has(tagName)) continue;

      if (isClosingTag(token)) {
        const wasProtecting = stack.pop();
        if (wasProtecting) protectedDepth--;
      } else {
        const isCopyBlock =
          tagName === "div" && getAttr(token, "data-ds-block") === "copy-block";
        const protects = NON_LINKABLE_TAGS.has(tagName) || isCopyBlock;
        stack.push(protects);
        if (protects) protectedDepth++;
      }
    } else {
      segments.push({ type: "text", value: token, protected: protectedDepth > 0 });
    }
  }

  return segments;
};

// -----------------------------------------------------------------------
// Casamento de keyword — acento e caixa não importam, mas palavra inteira sim
// -----------------------------------------------------------------------

// Mapa de "dobra" de acentuação. Importante: cada caractere acentuado vira
// EXATAMENTE um caractere ASCII (nunca uma forma decomposta com combining
// mark), porque o casamento é feito na string dobrada e os índices do match
// são usados depois para recortar a string ORIGINAL — se dobrar mudasse o
// comprimento, os índices desalinhariam e o link sairia deslocado.
const FOLD_MAP: Record<string, string> = {
  á: "a", à: "a", â: "a", ã: "a", ä: "a",
  é: "e", è: "e", ê: "e", ë: "e",
  í: "i", ì: "i", î: "i", ï: "i",
  ó: "o", ò: "o", ô: "o", õ: "o", ö: "o",
  ú: "u", ù: "u", û: "u", ü: "u",
  ç: "c", ñ: "n",
};

const fold = (s: string): string =>
  s.toLowerCase().replace(/[áàâãäéèêëíìîïóòôõöúùûüçñ]/g, (c) => FOLD_MAP[c] ?? c);

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const escapeAttr = (s: string): string => s.replace(/"/g, "&quot;");
const escapeText = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

interface KeywordMatch {
  segmentIndex: number;
  start: number;
  end: number;
}

/**
 * Procura, na ordem das keywords (prioridade) e depois na ordem do documento
 * (primeira ocorrência), um trecho de texto elegível (não protegido) que bata
 * com alguma keyword do alvo, respeitando limite de palavra — "crediário" não
 * pode casar dentro de "crediários". O `\p{L}` cobre letra unicode, mas como
 * o casamento roda sobre a string já dobrada (ASCII), na prática funciona
 * como um `\w` de verdade para português.
 */
const matchKeyword = (segments: Segment[], keywords: string[]): KeywordMatch | null => {
  for (const keyword of keywords) {
    const folded = escapeRegExp(fold(keyword.trim()));
    if (!folded) continue;
    const re = new RegExp(`(?<![\\p{L}\\p{N}_])${folded}(?![\\p{L}\\p{N}_])`, "u");

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      if (seg.type !== "text" || seg.protected) continue;

      const match = re.exec(fold(seg.value));
      if (match) {
        return { segmentIndex: i, start: match.index, end: match.index + match[0].length };
      }
    }
  }
  return null;
};

/**
 * Troca 1 segmento de texto por 5, envolvendo `[start, end)` num `<a>`. O
 * texto da âncora é o que já estava lá — nunca é reescrito — o que resolve a
 * regra 2 (âncora sai do texto existente) de graça.
 */
const insertLinkAt = (
  segments: Segment[],
  match: KeywordMatch,
  href: string
): string => {
  const seg = segments[match.segmentIndex] as Extract<Segment, { type: "text" }>;
  const before = seg.value.slice(0, match.start);
  const anchorText = seg.value.slice(match.start, match.end);
  const after = seg.value.slice(match.end);

  segments.splice(
    match.segmentIndex,
    1,
    { type: "text", value: before, protected: seg.protected },
    { type: "tag", value: `<a href="${escapeAttr(href)}">` },
    // A âncora recém-criada fica marcada como protegida: é assim que a regra
    // "uma ocorrência por destino" e a idempotência se resolvem sem estado
    // externo — na segunda passada esse trecho já não é mais texto elegível,
    // e o próprio href já vai estar em `existingHrefs` de qualquer forma.
    { type: "text", value: anchorText, protected: true },
    { type: "tag", value: "</a>" },
    { type: "text", value: after, protected: seg.protected }
  );

  return anchorText;
};

/** `/blog/<slug>` para pilar/satélite; para a LP, `slug` já É o caminho (ex.: "/crediario"). */
const resolveHref = (target: LinkTarget): string =>
  target.role === "landing" ? target.slug : `/blog/${target.slug}`;

// -----------------------------------------------------------------------
// extractInternalLinks
// -----------------------------------------------------------------------

/**
 * Lista os hrefs internos (`/algo`, nunca `//host` nem `http(s)://`) já
 * presentes num HTML de post. Usado tanto para a idempotência do
 * `planInternalLinks` quanto pelo job mensal (`findOrphans`), por isso vai
 * pela mesma tokenização de tags — evita casar `href="..."` que apareça
 * dentro de texto corrido (ex.: um post explicando HTML) em vez de dentro de
 * uma tag `<a>` de verdade.
 */
export const extractInternalLinks = (html: string): string[] => {
  const tokens = html.match(TOKEN_RE) ?? [];
  const hrefs: string[] = [];

  for (const token of tokens) {
    if (!token.startsWith("<") || isClosingTag(token)) continue;
    if (openTagName(token) !== "a") continue;

    const href = getAttr(token, "href");
    if (href && href.startsWith("/") && !href.startsWith("//")) {
      hrefs.push(href);
    }
  }

  return hrefs;
};

// -----------------------------------------------------------------------
// planInternalLinks
// -----------------------------------------------------------------------

export const planInternalLinks = (
  html: string,
  targets: LinkTarget[],
  options: PlanInternalLinksOptions = {}
): LinkPlan => {
  const {
    dryRun = false,
    minSatellites = DEFAULT_MIN_SATELLITES,
    maxSatellites = DEFAULT_MAX_SATELLITES,
  } = options;

  const segments = buildSegments(html);
  const inserted: LinkPlan["inserted"] = [];
  const skipped: LinkPlan["skipped"] = [];

  // Hrefs já presentes no HTML contam como "meta já atingida" — é o que
  // garante idempotência (rodar duas vezes não duplica) sem precisar de
  // nenhum estado fora da própria função.
  const usedHrefs = new Set(extractInternalLinks(html));

  const tryInsert = (target: LinkTarget): boolean => {
    const href = resolveHref(target);
    if (usedHrefs.has(href)) return true; // já linkado, nada a fazer

    const candidateKeywords = target.keywords.length > 0 ? target.keywords : [target.title];
    const match = matchKeyword(segments, candidateKeywords);

    if (match) {
      const anchor = insertLinkAt(segments, match, href);
      usedHrefs.add(href);
      inserted.push({ href, anchor, role: target.role });
      return true;
    }

    if (target.role === "landing") {
      // Única exceção da regra 2: a LP de conversão é o link mais importante
      // do post (é o dado dos concorrentes) e não pode ficar sem link de
      // entrada só porque o texto não menciona a LP literalmente. Quando não
      // há ocorrência natural, a âncora é inventada usando o mesmo formato do
      // bloco `cta-lead` já existente no registro de blocos — continua
      // passando pelo sanitizador normalmente, porque é feito só das tags e
      // atributos já permitidos.
      const anchor = `Conheça ${target.title}`;
      const block =
        `<div data-ds-block="cta-lead"><p>Quer ver isso funcionando na prática?</p>` +
        `<a href="${escapeAttr(href)}">${escapeText(anchor)}</a></div>`;
      segments.push({ type: "tag", value: block });
      usedHrefs.add(href);
      inserted.push({ href, anchor, role: target.role });
      return true;
    }

    skipped.push({ href, reason: "nenhuma keyword do alvo encontrada no texto corrido" });
    return false;
  };

  const landing = targets.find((t) => t.role === "landing");
  const pillar = targets.find((t) => t.role === "pillar");
  const satellites = targets.filter((t) => t.role === "satellite");

  if (landing) tryInsert(landing);
  else skipped.push({ href: "", reason: "nenhum alvo de landing informado" });

  if (pillar) tryInsert(pillar);
  else skipped.push({ href: "", reason: "nenhum alvo de pilar informado" });

  let satellitesLinked = 0;
  for (const target of satellites) {
    if (satellitesLinked >= maxSatellites) break;
    if (tryInsert(target)) satellitesLinked++;
  }
  if (satellitesLinked < minSatellites) {
    skipped.push({
      href: "",
      reason:
        `apenas ${satellitesLinked} satélite(s) linkado(s) de um mínimo de ${minSatellites} — ` +
        "faltou candidato ou nenhuma keyword bateu no texto",
    });
  }

  const finalHtml = segments.map((s) => s.value).join("");

  return { html: dryRun ? html : finalHtml, inserted, skipped };
};

// -----------------------------------------------------------------------
// findOrphans — usado pelo job mensal (E7.2) para achar posts sem link de entrada
// -----------------------------------------------------------------------

/** O suficiente do post para localizá-lo entre os links (`/blog/<slug>`). */
export interface OrphanCandidate {
  slug: string;
}

/**
 * Post "órfão" é aquele para o qual nenhum outro post do blog aponta. Recebe
 * o HTML de TODOS os posts (`allHtml`, mapeado por slug) porque um link de
 * entrada pode vir de qualquer post do cluster, não só dos vizinhos diretos —
 * varrer todo mundo é o único jeito correto de responder "ninguém aponta pra
 * este daqui".
 */
export const findOrphans = <T extends OrphanCandidate>(
  posts: T[],
  allHtml: Record<string, string>
): T[] => {
  const linkedSlugs = new Set<string>();
  for (const html of Object.values(allHtml)) {
    for (const href of extractInternalLinks(html)) {
      const match = /^\/blog\/([^/?#]+)/.exec(href);
      if (match) linkedSlugs.add(match[1]);
    }
  }
  return posts.filter((post) => !linkedSlugs.has(post.slug));
};
